import classNames from 'classnames'
import { SyncMultipleBar, SyncMultipleDonut, LineChart as DSLineChart } from '@winnin/ds-charts'
import { Button } from '@winnin/winnin_ui.components.button'
import { Title } from '@winnin/winnin_ui.components.title'

import { BarChart, ShareChart, LineChart } from 'assets/icons'
import { BarsChartSkeleton } from 'components/bars-chart-skeleton'
import LoadingErrorWrapper from 'components/LoadingErrorWrapper'
import PrintButton from 'components/PrintButton'
import { SelectWithIcon } from 'components/select-with-icon'
import { useTranslate } from 'lib/tolgee'
import { humanizeLargeNumber, TOLGEE_GLOBALS } from 'utils'

import type { TNicheChartSelector } from './niche-chart-selector.types'

import './niche-chart-selector.styles.scss'

export const NicheChartSelectorView = ({
  selectChartType,
  selectedChartType,
  handleOpenDashboardModal,
  isChartReady,
  chartOptions,
  shouldDisplayChartDropdown,
  title,
  chartKey,
  shareChartData,
  shareChartRef,
  handlePrintChart,
  chartExportOptions,
  barChartData,
  barChartRef,
  lineChartRef,
  lineChartData,
  lineChartDataLabels,
  error,
  loading,
  refetch,
  shouldDisableButtons,
}: TNicheChartSelector) => {
  const t = useTranslate()
  return (
    <div className="NicheChartSelector">
      <div className="NicheChartSelector-titleRow">
        <Title tag="h3">{title}</Title>
        <div className="NicheChartSelector-chartActions">
          <PrintButton
            chartTitle={title}
            onExport={handlePrintChart}
            disabled={shouldDisableButtons}
            color="var(--color_neutral_800)"
          />
          {shouldDisplayChartDropdown && <SelectWithIcon icon="upDownArrows" options={chartOptions} />}
          <div className="NicheChartSelector-chartTypeSelector">
            <button
              onClick={() => selectChartType('bar')}
              className={classNames(['NicheChartSelector-chartButton', { isActive: selectedChartType === 'bar' }])}
              disabled={shouldDisableButtons}
            >
              <BarChart />
            </button>
            <button
              onClick={() => selectChartType('line')}
              className={classNames(['NicheChartSelector-chartButton', { isActive: selectedChartType === 'line' }])}
              disabled={shouldDisableButtons}
            >
              <LineChart />
            </button>
            <button
              onClick={() => selectChartType('donut')}
              className={classNames(['NicheChartSelector-chartButton', { isActive: selectedChartType === 'donut' }])}
              disabled={shouldDisableButtons}
            >
              <ShareChart />
            </button>
          </div>
          {handleOpenDashboardModal && (
            <Button appearance="outline" onClick={handleOpenDashboardModal} disabled={shouldDisableButtons}>
              {t(TOLGEE_GLOBALS.createReport)}
            </Button>
          )}
        </div>
      </div>
      <div className="NicheChartSelector-chartContent">
        {isChartReady ? (
          <>
            {selectedChartType === 'bar' && (
              <LoadingErrorWrapper
                error={error}
                loading={loading}
                refetch={refetch}
                skeletonComponent={BarsChartSkeleton}
              >
                <div className="NicheChartSelector-chart">
                  <SyncMultipleBar
                    chartsGroup={barChartData}
                    ref={barChartRef}
                    syncOptions={['tooltip', 'highlight']}
                    tooltip={{
                      type: 'default',
                      formatter: value => humanizeLargeNumber(value, 1),
                    }}
                    hasLegend
                    maxLegendHeight={80}
                    height="420px"
                    xAxisOptions={{
                      labelFormatter: value => humanizeLargeNumber(value, 1),
                    }}
                    exportOptions={chartExportOptions}
                  />
                </div>
              </LoadingErrorWrapper>
            )}
            {selectedChartType === 'line' && (
              <LoadingErrorWrapper
                error={error}
                loading={loading}
                refetch={refetch}
                skeletonComponent={BarsChartSkeleton}
              >
                <DSLineChart
                  entities={lineChartData}
                  ref={lineChartRef}
                  tooltip={{
                    type: 'default',
                    shared: true,
                    props: {
                      outside: true,
                    },
                    formatter: value => humanizeLargeNumber(value, 1),
                  }}
                  xAxisLabels={lineChartDataLabels}
                  legend={{
                    type: 'default',
                    maxHeight: 120,
                  }}
                  height="500px"
                  customOptions={{
                    xAxis: {
                      allowDecimals: false,
                    },
                    yAxis: {
                      labels: {
                        formatter: ({ value }) => humanizeLargeNumber(value, 1),
                      },
                    },
                  }}
                  exportOptions={chartExportOptions}
                />
              </LoadingErrorWrapper>
            )}
            {selectedChartType === 'donut' && (
              <LoadingErrorWrapper
                error={error}
                loading={loading}
                refetch={refetch}
                skeletonComponent={BarsChartSkeleton}
              >
                <div className="NicheChartSelector-chart">
                  <SyncMultipleDonut
                    ref={shareChartRef}
                    key={chartKey}
                    chartsGroup={shareChartData}
                    syncOptions={['highlight', 'tooltip']}
                    tooltip={{ type: 'inline', valueFormatType: 'percent', props: { followPointer: false } }}
                    yAxisOptions={{ labelFormatType: 'percent' }}
                    dataLabels={{
                      filter: { operator: '>', property: 'percentage', value: 2 },
                      style: { fontSize: '12px' },
                    }}
                    hasLegend
                    maxLegendHeight={80}
                    height="420px"
                    exportOptions={chartExportOptions}
                  />
                </div>
              </LoadingErrorWrapper>
            )}
          </>
        ) : (
          <BarsChartSkeleton />
        )}
      </div>
    </div>
  )
}
